<template>
    <div class="device-controls__block">
        <div class="device-controls__row device-controls__row--title">
            <span class="dci__title">
                <span class="sg-vrm-icon_113_inverter-24px"></span>
                {{ formattedName }}
            </span>
            <div v-if="isWaitingForResponse" class="dci__status">
                <spinner />
                <span>
                    {{ $t('dashboard.dci.loading') }}
                </span>
            </div>
        </div>
        <div
            v-if="changeModeTimer.nextChange !== null"
            :style="{ height: bodyHeight + 'px' }"
            class="device-controls__body"
        >
            <change-warning
                @cancel="changeModeTimer.clearChangeTimer()"
                @confirm="changeModeTimer.confirmChange()"
            >
                <div
                    v-html="
                        $t('dashboard.dci.countdown_timer', {
                            nextState: $t(
                                `dashboard.dci.multi_rs.radio.${getRadioValue(
                                    changeModeTimer.nextChange,
                                )}`,
                            ),
                            seconds: timerLimit - changeModeTimer.changeSeconds,
                        })
                    "
                ></div>
            </change-warning>
        </div>

        <div v-else ref="multiRsBody" class="device-controls__body device-controls__body--inverter">
            <div class="device-controls__row">
                <h4
                    class="dci__subtitle"
                    :class="{ 'dci__subtitle--disabled': isWaitingForResponse }"
                >
                    {{ $t(`dashboard.dci.mode`, { i18nFallback: 'Mode:' }) }}
                </h4>
                <span v-if="multiModeChange.error" class="dci__warning dci__warning--inline">
                    {{ $t('dashboard.dci.errors.' + multiModeChange.error) }}
                </span>
            </div>

            <div
                class="
                device-controls-cta-section
                device-controls-cta-section--checkboxes
            "
                :class="{
                    'device-controls-cta-section--disabled': isWaitingForResponse,
                }"
            >
                <div
                    v-for="radioValue in radioValues"
                    :key="radioValue.valueEnum"
                    class="device-controls__row device-controls__row--checkbox  "
                    :class="{ 'device-controls__row--disabled': isWaitingForResponse }"
                    @click="
                        () => {
                            if (!isWaitingForResponse && multiMode !== radioValue.valueEnum)
                                changeModeWithTimer(radioValue.valueEnum);
                        }
                    "
                >
                    <span>
                        {{
                            $t(`dashboard.dci.multi_rs.radio.${radioValue.nameEnum}`, {
                                i18nFallback: radioValue.nameEnum,
                            })
                        }}
                    </span>
                    <div class="vrm-form__custom-radio-checkbox ">
                        <div class="sg-custom-check-and-radio custom-radio ">
                            <label>
                                <input
                                    type="radio"
                                    :checked="multiMode === radioValue.valueEnum"
                                    :class="{ 'sg-disabled': isWaitingForResponse }"
                                />
                                <div class="sg-alternative-display">
                                    <div class="sg-outer">
                                        <div class="sg-inner"></div>
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { ngInject } from '@/vue/decorators/angular-interop';
import { createTimer } from './components/Timer';

import {
    getMultiRsSubscriptions,
    subscribeToInfo,
    unsubscribeFromInfo,
    publishToPath,
} from './utils';

import { newMqttWriteValue, initiateValueLoading, resolveValue } from './writeValueUtils';
import Spinner from '@/vue/components/shared/device-controls/controls/components/Spinner';
import ChangeWarning from '@/vue/components/shared/device-controls/controls/components/ChangeWarning';

@Component({
    components: {
        Spinner,
        ChangeWarning,
    },
})
@AngularLocaleBundle('dashboard')
@AngularLocaleBundle('common')
export default class MultiRs extends Vue {
    @ngInject()
    MQTTRealtimeService;
    @ngInject()
    ApiService;

    @Prop({ type: Number, required: true })
    instance;
    @Prop({ type: Number, required: true })
    installationId;
    @Prop({ type: String, required: true })
    identifier;
    @Prop({ type: Number, required: true })
    timerLimit;

    radioValues = [
        { nameEnum: 'Off', valueEnum: 4 },
        { nameEnum: 'Charger Only', valueEnum: 1 },
        { nameEnum: 'Inverter Only', valueEnum: 2 },
        { nameEnum: 'On', valueEnum: 3 },
        { nameEnum: 'Passthrough', valueEnum: 251 },
    ];

    SUBSCRIPTIONS = getMultiRsSubscriptions();

    // controlled by mqtt
    multiMode = null;
    customName = null;
    productName = null;

    // inherited from createTimer at mount
    changeModeTimer = {};
    selectedMultiMode = null;

    multiModeChange = newMqttWriteValue({ varName: 'multiMode' });

    bodyHeight = 187;

    created() {
        createTimer.call(this, 'changeModeTimer', this.timerLimit);
    }

    mounted() {
        this.subscribeToInfo = subscribeToInfo.bind(this);
        this.unsubscribeFromInfo = unsubscribeFromInfo.bind(this);
        this.publishToPath = publishToPath.bind(this);
        this.initiateValueLoading = initiateValueLoading.bind(this);
        this.resolveValue = resolveValue.bind(this);
        this.subscribeToInfo();
    }

    beforeDestroy() {
        window.clearTimeout(this.multiModeChange.timer);
        this.unsubscribeFromInfo();
    }

    @Watch('multiMode')
    onMultiMode() {
        this.resolveValue(this.multiModeChange, this.multiMode);
    }

    get isWaitingForResponse() {
        return this.multiModeChange.busy;
    }

    changeModeWithTimer(value) {
        if (this.isWaitingForResponse) return;
        this.matchBodyHeight();
        this.selectedMultiMode = value;
        this.changeModeTimer.callFuncWithTimer(value, this.changeMode);
    }

    changeMode(value) {
        this.multiModeChange.oldState = this.multiMode;
        this.multiModeChange.newState = value;
        this.initiateValueLoading(this.multiModeChange);
        this.publishToPath(
            {
                dbusPath: '/Mode',
                callbackIdentifier: `setModeOfMultiRs_${this.instance}`,
            },
            { value },
            this.multiMode,
        );
    }

    getRadioValue(value) {
        const foundValue = this.radioValues.find(radioValue => radioValue.valueEnum == value);

        if (foundValue) {
            return foundValue.nameEnum;
        }
        return null;
    }

    getCallbackIdentifier(varName) {
        return `get_${varName}OfMultiRs_${this.instance}`;
    }

    get formattedName() {
        if (this.customName) {
            return this.customName;
        }
        if (this.productName) {
            return this.productName + ` #${this.instance}`;
        }
        return `Multi RS #${this.instance}`;
    }

    get mqttPathObj() {
        return {
            identifier: this.identifier,
            dbusServiceType: 'multi',
            instance: this.instance,
        };
    }

    matchBodyHeight() {
        if (this.$refs.multiRsBody !== undefined)
            this.bodyHeight = this.$refs.multiRsBody.clientHeight;
    }
}
</script>
